import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { MessageSquare, Send, Lock, Unlock, Zap, CreditCard } from "lucide-react";

function Counter({ cfg, onBalance }) {
  const [c, setC] = useState(null);
  const [showRecharge, setShowRecharge] = useState(false);
  const [units, setUnits] = useState("");
  const [note, setNote] = useState("");
  useEffect(() => { api.get("/waba/counter").then(r => { setC(r.data); onBalance?.(r.data.balance_remaining); }).catch(() => setC({ total_allocated: 0, total_sent: 0, balance_remaining: 0, billing_cycle: "", _err: true })); }, []);
  const submit = async () => {
    try { await api.post("/waba/recharge-request", { units: Number(units) || null, note }); toast.success("Recharge request sent to Ponderr Admin"); setShowRecharge(false); setUnits(""); setNote(""); }
    catch { toast.error("Failed"); }
  };
  if (!c) return <div className="nb-card p-5 bg-[#C4B5FD]" data-testid="waba-counter">Loading quota…</div>;
  const exhausted = c.balance_remaining <= 0;
  return (
    <div className={`nb-card p-5 ${exhausted ? "bg-[#FCA5A5]" : "bg-[#C4B5FD]"}`} data-testid="waba-counter">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="display text-lg font-bold flex items-center gap-2"><MessageSquare size={18} /> WhatsApp Quota · {c.billing_cycle}</h3>
          <div className="flex gap-4 mt-2 text-sm">
            <div><b className="text-2xl display" data-testid="quota-allocated">{c.total_allocated}</b><div className="nb-label">Total Allocated</div></div>
            <div><b className="text-2xl display" data-testid="quota-sent">{c.total_sent}</b><div className="nb-label">Sent This Cycle</div></div>
            <div><b className="text-2xl display" data-testid="quota-balance">{c.balance_remaining}</b><div className="nb-label">Balance Available</div></div>
          </div>
        </div>
        <button className="nb-btn nb-btn-tertiary" onClick={() => setShowRecharge(true)} data-testid="waba-recharge-cta"><CreditCard size={16} /> Request Message Recharge</button>
      </div>
      {exhausted && <div className="mt-3 nb-card-sm p-2 bg-white text-sm font-bold" data-testid="quota-exhausted-alert">WhatsApp message balance exhausted. Please request a recharge.</div>}
      {showRecharge && (
        <div className="fixed inset-0 bg-black/40 grid place-items-center z-50" onClick={() => setShowRecharge(false)}>
          <div className="nb-card p-6 bg-white w-full max-w-md space-y-3" onClick={e => e.stopPropagation()} data-testid="waba-recharge-modal">
            <h3 className="display text-xl font-bold">Request Message Recharge</h3>
            <input className="nb-input" placeholder="Units needed (optional)" value={units} onChange={e => setUnits(e.target.value)} />
            <textarea className="nb-textarea" rows={3} placeholder="Note to Ponderr Admin" value={note} onChange={e => setNote(e.target.value)} />
            <div className="flex gap-2">
              <button className="nb-btn flex-1 justify-center" onClick={submit} data-testid="waba-recharge-submit">Send Request</button>
              <button className="nb-btn nb-btn-ghost" onClick={() => setShowRecharge(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FeeGateCard({ cfg, reload }) {
  const [reminder, setReminder] = useState(cfg.reminder_threshold);
  const [gate, setGate] = useState(cfg.gate_threshold);
  const [override, setOverride] = useState(cfg.school_wide_override);
  const [preview, setPreview] = useState(null);
  const loadPreview = () => api.get("/waba/fee-preview").then(r => setPreview(r.data)).catch(() => {});
  useEffect(() => { loadPreview(); }, []);
  const save = async () => {
    try { await api.put("/waba/config", { reminder_threshold: Number(reminder), gate_threshold: Number(gate) }); toast.success("Thresholds saved"); reload(); loadPreview(); }
    catch { toast.error("Failed"); }
  };
  const toggleGates = async () => {
    try { const r = await api.post("/waba/lift-gates", { school_wide_override: !override }); setOverride(r.data.school_wide_override); toast.success(r.data.school_wide_override ? "Gates lifted school-wide" : "Gates re-applied"); reload(); loadPreview(); }
    catch { toast.error("Failed"); }
  };
  const sendReminders = async () => {
    try { const r = await api.post("/waba/send/fee-reminders", { reminder_threshold: Number(reminder) }); toast.success(`Reminders sent → ${r.data.sent}${r.data.simulated ? " (SIMULATED)" : ""}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const gatedCount = preview?.students.filter(s => s.gated).length || 0;
  const remindCount = preview?.students.filter(s => s.reminder_flagged).length || 0;
  return (
    <div className="nb-card p-5 bg-white space-y-3" data-testid="waba-fee-gate">
      <h3 className="display text-lg font-bold">Fee Due Reminders & Fee Gate Policy</h3>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="nb-label">Reminder Threshold (₹)</label>
          <input type="number" className="nb-input mt-1" value={reminder} onChange={e => setReminder(e.target.value)} data-testid="waba-reminder-threshold" />
          <p className="text-xs opacity-60 mt-1">{remindCount} student(s) flagged for reminders.</p>
        </div>
        <div>
          <label className="nb-label">Gate Threshold (₹) · 0 = off</label>
          <input type="number" className="nb-input mt-1" value={gate} onChange={e => setGate(e.target.value)} data-testid="waba-gate-threshold" />
          <p className="text-xs opacity-60 mt-1">{gatedCount} student(s) would have Marks/Report Card tabs locked.</p>
        </div>
      </div>
      <div className="flex gap-2 flex-wrap">
        <button className="nb-btn" onClick={save} data-testid="waba-save-thresholds">Save Thresholds</button>
        <button className={`nb-btn ${override ? "nb-btn-secondary" : "nb-btn-tertiary"}`} onClick={toggleGates} data-testid="waba-lift-gates">
          {override ? <><Unlock size={14} /> Gates Lifted (click to re-apply)</> : <><Lock size={14} /> Lift Gates School-Wide</>}
        </button>
        <button className="nb-btn nb-btn-info" onClick={sendReminders} data-testid="waba-send-reminders"><Send size={14} /> Send Fee Reminders</button>
      </div>
    </div>
  );
}

function HolidayCard({ disabled }) {
  const [f, setF] = useState({ holiday_date: "", occasion: "", resumption_date: "" });
  const send = async () => {
    try { const r = await api.post("/waba/send/holiday", f); toast.success(`Holiday notice sent → ${r.data.sent}${r.data.simulated ? " (SIMULATED)" : ""}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="nb-card p-5 bg-[#FDE047] space-y-2" data-testid="waba-card-holiday">
      <h3 className="display text-lg font-bold">Holiday Announcement</h3>
      <input type="date" className="nb-input text-sm" value={f.holiday_date} onChange={e => setF({ ...f, holiday_date: e.target.value })} />
      <input className="nb-input text-sm" placeholder="Occasion" value={f.occasion} onChange={e => setF({ ...f, occasion: e.target.value })} />
      <input type="date" className="nb-input text-sm" value={f.resumption_date} onChange={e => setF({ ...f, resumption_date: e.target.value })} />
      <button className="nb-btn w-full justify-center" disabled={disabled} onClick={send} data-testid="waba-send-holiday">Send Holiday Notice</button>
    </div>
  );
}

function GradeMultiSelect({ grades, selected, setSelected, testid }) {
  const [open, setOpen] = useState(false);
  const all = !selected || selected.length === 0;
  const label = all ? "All Grades" : `${selected.length} grade${selected.length > 1 ? "s" : ""} selected`;
  const toggle = (g) => setSelected(selected.includes(g) ? selected.filter(x => x !== g) : [...selected, g]);
  return (
    <div className="relative" data-testid={testid}>
      <button type="button" onClick={() => setOpen(o => !o)} className="nb-input text-sm w-full text-left flex justify-between items-center" data-testid={`${testid}-btn`}>
        <span>{label}</span><span className="opacity-50">▾</span>
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-full max-h-56 overflow-auto nb-card bg-white p-2 shadow-2xl" data-testid={`${testid}-menu`}>
          {(!grades || grades.length === 0) && <div className="text-xs opacity-60 p-1">No grades in School Settings</div>}
          <label className="flex items-center gap-2 text-sm p-1 cursor-pointer font-bold">
            <input type="checkbox" checked={all} onChange={() => setSelected([])} data-testid={`${testid}-all`} /> All Grades
          </label>
          {(grades || []).map(g => (
            <label key={g} className="flex items-center gap-2 text-sm p-1 cursor-pointer">
              <input type="checkbox" checked={selected.includes(g)} onChange={() => toggle(g)} data-testid={`${testid}-${g}`} /> {g}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

function HomeworkCard({ disabled, grades }) {
  const [f, setF] = useState({ break_period: "", deadline_date: "" });
  const [cls, setCls] = useState([]);
  const send = async () => {
    try { const r = await api.post("/waba/send/holiday-homework", { ...f, class_names: cls }); toast.success(`Homework alert sent → ${r.data.sent}${r.data.simulated ? " (SIMULATED)" : ""}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="nb-card p-5 bg-[#86EFAC] space-y-2" data-testid="waba-card-homework">
      <h3 className="display text-lg font-bold">Holiday Homework</h3>
      <label className="nb-label">Grades</label>
      <GradeMultiSelect grades={grades} selected={cls} setSelected={setCls} testid="waba-homework-grades" />
      <input className="nb-input text-sm" placeholder="Break period e.g. Summer Break" value={f.break_period} onChange={e => setF({ ...f, break_period: e.target.value })} />
      <input type="date" className="nb-input text-sm" value={f.deadline_date} onChange={e => setF({ ...f, deadline_date: e.target.value })} />
      <button className="nb-btn w-full justify-center" disabled={disabled} onClick={send} data-testid="waba-send-homework">Send Homework Alert</button>
    </div>
  );
}

function MarksCardDispatch({ disabled, grades }) {
  const [f, setF] = useState({ exam_title: "", session_str: "", document_url: "", file_name: "MarksCard.pdf", overall_result: "" });
  const [cls, setCls] = useState([]);
  const [preview, setPreview] = useState([]);
  const [selected, setSelected] = useState({});
  const load = () => api.get("/waba/fee-preview", { params: cls.length ? { class_names: cls.join(",") } : {} }).then(r => {
    setPreview(r.data.students);
    const init = {}; r.data.students.forEach(s => { init[s.student_id] = !s.gated; });
    setSelected(init);
  }).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cls]);
  const send = async () => {
    const ids = Object.keys(selected).filter(k => selected[k]);
    const override_ids = preview.filter(s => s.gated && selected[s.student_id]).map(s => s.student_id);
    try { const r = await api.post("/waba/send/marks-card", { ...f, student_ids: ids, override_ids }); toast.success(`Marks cards dispatched → ${r.data.sent}${r.data.simulated ? " (SIMULATED)" : ""}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="nb-card p-5 bg-white space-y-2 lg:col-span-2" data-testid="waba-card-marks">
      <h3 className="display text-lg font-bold">Marks Card Dispatch</h3>
      <label className="nb-label">Grades</label>
      <GradeMultiSelect grades={grades} selected={cls} setSelected={setCls} testid="waba-marks-grades" />
      <div className="grid sm:grid-cols-3 gap-2">
        <input className="nb-input text-sm" placeholder="Exam title" value={f.exam_title} onChange={e => setF({ ...f, exam_title: e.target.value })} />
        <input className="nb-input text-sm" placeholder="Session e.g. 2026-27" value={f.session_str} onChange={e => setF({ ...f, session_str: e.target.value })} />
        <input className="nb-input text-sm" placeholder="Overall result" value={f.overall_result} onChange={e => setF({ ...f, overall_result: e.target.value })} />
        <input className="nb-input text-sm sm:col-span-3" placeholder="PDF document URL (https…)" value={f.document_url} onChange={e => setF({ ...f, document_url: e.target.value })} />
      </div>
      <div className="max-h-64 overflow-auto border-2 border-black rounded-lg">
        <table className="nb-table text-sm">
          <thead><tr><th>Send</th><th>Student</th><th>Class</th><th>Outstanding</th><th>Status</th></tr></thead>
          <tbody>
            {preview.map(s => (
              <tr key={s.student_id} className={s.gated ? "opacity-50" : ""} data-testid={`waba-marks-row-${s.student_id}`}>
                <td><input type="checkbox" checked={!!selected[s.student_id]} onChange={e => setSelected(x => ({ ...x, [s.student_id]: e.target.checked }))} data-testid={`waba-marks-check-${s.student_id}`} /></td>
                <td className="font-bold">{s.name}</td>
                <td>{s.grade_section}</td>
                <td className="mono">₹{s.outstanding}</td>
                <td>{s.gated ? <span className="nb-badge bg-[#FCA5A5] flex items-center gap-1 w-fit"><Lock size={10} />Gated</span> : <span className="nb-badge bg-[#86EFAC]">OK</span>}</td>
              </tr>
            ))}
            {preview.length === 0 && <tr><td colSpan={5} className="text-center py-4 text-slate-500">No students</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="text-xs opacity-60">Gated rows are greyed with an unchecked box — tick to override for this session.</p>
      <button className="nb-btn w-full justify-center" disabled={disabled} onClick={send} data-testid="waba-send-marks"><Send size={14} /> Dispatch Marks Cards</button>
    </div>
  );
}

function AttendanceAutomation({ cfg, reload }) {
  const [time, setTime] = useState(cfg.absent_alert_time || "10:00");
  const [status, setStatus] = useState(null);
  const load = () => api.get("/waba/attendance-status").then(r => setStatus(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const save = async () => {
    try { await api.put("/waba/config", { absent_alert_time: time }); toast.success("Alert time saved"); reload(); load(); }
    catch { toast.error("Failed"); }
  };
  return (
    <div className="nb-card p-5 bg-[#93C5FD] space-y-3" data-testid="waba-attendance-automation">
      <h3 className="display text-lg font-bold">Attendance Automation Settings</h3>
      <div className="flex items-end gap-2 flex-wrap">
        <div><label className="nb-label">Daily Absent Alert Time (IST)</label>
          <input type="time" className="nb-input mt-1" value={time} onChange={e => setTime(e.target.value)} data-testid="waba-alert-time" /></div>
        <button className="nb-btn" onClick={save} data-testid="waba-save-alert-time">Save Time</button>
        <button className="nb-btn nb-btn-ghost" onClick={load} data-testid="waba-sync-holidays">+ Sync Holiday Dates</button>
      </div>
      {status && (
        <>
          <div className={`nb-card-sm p-3 text-sm ${status.is_holiday_today ? "bg-[#FCA5A5]" : "bg-[#86EFAC]"}`} data-testid="waba-attendance-status">
            {status.is_holiday_today ? "Today: School Holiday (Alerts Suppressed)" : `Today: Regular School Day (Alerts Active at ${status.alert_time} IST)`}
          </div>
          <div>
            <label className="nb-label">Upcoming Holidays (from Settings)</label>
            <div className="flex flex-wrap gap-1 mt-1">
              {(status.upcoming_holidays || []).length === 0 ? <span className="text-xs opacity-70">None synced — add under Settings → Holidays.</span>
                : status.upcoming_holidays.map(d => <span key={d} className="nb-badge bg-white text-xs">{d}</span>)}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function FeeReceiptRules({ cfg, reload }) {
  const [text, setText] = useState(cfg.fee_send_text);
  const [pdf, setPdf] = useState(cfg.fee_send_pdf);
  const save = async (nt, np) => {
    try { await api.put("/waba/config", { fee_send_text: nt, fee_send_pdf: np }); reload(); }
    catch { toast.error("Failed"); }
  };
  return (
    <div className="nb-card p-5 bg-[#FDE047] space-y-2" data-testid="waba-fee-receipt-rules">
      <h3 className="display text-lg font-bold">Fee Receipt Automation · Plan: {cfg.plan_name}</h3>
      <p className="text-xs opacity-70">Starter = text only · Growth = PDF only · Enterprise = both. Options your plan doesn't include are greyed out.</p>
      <label className={`flex items-center gap-2 text-sm ${cfg.fee_text_allowed ? "" : "opacity-40"}`}>
        <input type="checkbox" checked={!!text} disabled={!cfg.fee_text_allowed} onChange={e => { setText(e.target.checked); save(e.target.checked, pdf); }} data-testid="waba-fee-text" /> Send Confirmation Text
      </label>
      <label className={`flex items-center gap-2 text-sm ${cfg.fee_pdf_allowed ? "" : "opacity-40"}`}>
        <input type="checkbox" checked={!!pdf} disabled={!cfg.fee_pdf_allowed} onChange={e => { setPdf(e.target.checked); save(text, e.target.checked); }} data-testid="waba-fee-pdf" /> Send PDF Receipt
      </label>
    </div>
  );
}

export default function SchoolWaba() {
  const [cfg, setCfg] = useState(null);
  const [grades, setGrades] = useState([]);
  const [balance, setBalance] = useState(1);
  const load = () => api.get("/waba/config").then(r => setCfg(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/settings").then(r => setGrades(r.data.grades || [])).catch(() => {}); }, []);
  if (!cfg) return <div className="nb-card p-6">Loading WhatsApp controls…</div>;

  if (!cfg.waba_enabled) {
    return <div className="nb-card p-6 bg-[#FCA5A5]" data-testid="waba-disabled-banner">
      <h3 className="display text-lg font-bold">WhatsApp is disabled for your school</h3>
      <p className="text-sm mt-1">Contact Ponderr Admin to enable WhatsApp Business messaging.</p>
    </div>;
  }
  const exhausted = balance <= 0;
  const manualBlocked = !cfg.school_admin_messaging || exhausted;
  const tip = !cfg.school_admin_messaging ? "Manual WhatsApp sending is disabled for your school by Ponderr Admin"
    : exhausted ? "WhatsApp message balance exhausted — request a recharge" : "";
  return (
    <div className="space-y-5" data-testid="school-waba-panel">
      <div>
        <h2 className="display text-2xl font-bold">WhatsApp Alerts · MSG91 WABA</h2>
        <p className="text-sm opacity-70">Automated fee & attendance alerts + manual broadcasts. {manualBlocked && <b className="text-rose-700">{tip}.</b>}</p>
      </div>
      <Counter cfg={cfg} onBalance={setBalance} />
      <div className="grid lg:grid-cols-2 gap-4">
        <AttendanceAutomation cfg={cfg} reload={load} />
        <FeeReceiptRules cfg={cfg} reload={load} />
      </div>
      <FeeGateCard cfg={cfg} reload={load} />
      {manualBlocked && <div className="nb-card p-3 bg-slate-100 text-sm flex items-center gap-2" data-testid="waba-manual-blocked"><Lock size={14} />{tip}. The dispatch cards below are disabled.</div>}
      <div className="grid lg:grid-cols-2 gap-4" title={tip}>
        <HolidayCard disabled={manualBlocked} />
        <HomeworkCard disabled={manualBlocked} grades={grades} />
        <MarksCardDispatch disabled={manualBlocked} grades={grades} />
      </div>
    </div>
  );
}
